import { action, computed, observable, reaction, runInAction } from 'mobx';
import alertToast from 'Util/ToastifyUtils';
import { CreditProviderDto, ICreditProviderListStore } from '../types/CreditProviderListStoreTypes';
import { CreditProviderEndpoints } from './../endpoints/CreditProviderEndpoints';

export class CreditProviderListStore implements ICreditProviderListStore {
	@observable isLoading: boolean = false;
	@observable hasError: boolean = false;
	@observable creditProviders: CreditProviderDto[] = [];
	@observable pageNo: number = 1;
	@observable totalCount: number = 0;

	private readonly DEFAULT_PAGE_SIZE = 10;

	constructor() {
		this.getCreditProviders();

		reaction(
			() => this.pageNo,
			() => {
				this.getCreditProviders();
			}
		);
	}

	@action async reset() {
		await this.getCreditProviders();
	}

	@computed get pageSize() {
		return this.DEFAULT_PAGE_SIZE;
	}

	@action setIsLoading = (isLoading: boolean) => {
		this.isLoading = isLoading;
	}

	@action setCreditProviderStatus = async (creditProviderId: string, isActive: boolean) => {
		const result = await CreditProviderEndpoints.updateCreditProvider(creditProviderId, {
			isActive: isActive
		});
		runInAction(() => {
			if (!result) {
				console.error("Error updating credit provider status");
				alertToast("Error updating credit provider status", "error");
				this.clearFields();
				this.hasError = true;
				return;
			}
			
			const creditProvider = this.creditProviders.find(x => x.id === creditProviderId);
			if (creditProvider) {
				creditProvider.isActive = isActive;
				this.updateCreditProvider(creditProvider);
			}

			alertToast("Credit provider status updated successfully", "success");
		})
	}

	@action getCreditProviders = async () => {
		this.setIsLoading(true);
		this.hasError = false;
		const result = await CreditProviderEndpoints.getCreditProviders(this.pageNo, this.pageSize);
		runInAction(() => {
			this.creditProviders = result.data;
			this.totalCount = result.totalCount;
			this.setPageNo(result.pageNo);
			this.setIsLoading(false);
		})
	}

	@action clearFields = () => {
		this.creditProviders = [];
		this.setPageNo(1);
		this.totalCount = 0;
		this.hasError = false;
		this.isLoading = false;
	}

	@action setPageNo = (pageNo: number) => {
		if (pageNo !== this.pageNo) {
			this.pageNo = pageNo;
		}
	}

	@action private updateCreditProvider = (creditProvider: CreditProviderDto) => {
		const index = this.creditProviders.findIndex(x => x.id === creditProvider.id);
		if (index > -1) {
			this.creditProviders[index] = creditProvider;
		}
	}
}